import { Overlay } from '@angular/cdk/overlay';
import {
  ApplicationRef,
  Component,
  DestroyRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  SignInWithApple,
  SignInWithAppleOptions,
  SignInWithAppleResponse,
} from '@capacitor-community/apple-sign-in';
import { App } from '@capacitor/app';
import {
  Capacitor,
  PluginListenerHandle,
  SystemBars,
  SystemBarsStyle,
  SystemBarType,
} from '@capacitor/core';
import { Device, DeviceInfo } from '@capacitor/device';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import {
  GoogleLoginResponseOnline,
  SocialLogin,
} from '@capgo/capacitor-social-login';
import { Platform } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, fromEvent, interval } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { APPMODE } from 'src/environments/appmode.enum';
import { environment } from '../environments/environment';
import { defaultPaging } from './api/types/Pageable';
import { NewServiceWorkerVersion } from './application-state/+state/application-state.actions';
import { OrderDirectPrint } from './printers/+state/printers.actions';
import { HelperService } from './public/helper.service';
import { ClearIntegrationPayment } from './public/payments/+state/payment.actions';
import {
  SaveStoreByAlias,
  SaveStoreByAliasFailed,
  SaveStoreByAliasSuccess,
  SetMobileStore,
  StoreActionType,
} from './public/store/+state/stores.actions';
import { LogService } from './shared/logging/LogService';
import { MessageService } from './shared/message.service';
import { MobilePushNotificationService } from './shared/mobile-push-notification.service';
import { LocationService } from './public/location.service';
import { TrackingService } from './public/tracking.service';
import { AiWidgetBridgeService } from './shared/ai-widget-bridge.service';
import { ViewportService } from './shared/viewport.service';
import { WhitelabelService } from './shared/whitelabel.service';
import { getNotificationSubscriptionsByStoreId } from './stores/+state/stores.selectors';
import { AdminBarcodeScannerService } from './stores/store-events-bookings/stores-events-barcode-scanning-modal/admin-barcode-scanner.service';
import {
  ClientStore,
  Order,
  StoreSaveMode,
  StoreViewState,
} from './stores/stores';
import { StoresService } from './stores/stores.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  getCurrentCartState,
  getCurrentStoreViewStatus,
  getSelectedStore,
} from './public/store/+state/stores.selectors';
import { PaymentRedirectService } from './public/payments/payment-redirect.service';

export let browserRefresh = false;

interface QueryParams {
  [key: string]: string | number | boolean;
}

interface DeeplinkRoute {
  appEnvironment: string;
  fragment: string;
  path: string;
  queryParams: QueryParams;
  storeAlias: string;
  url: string;
}
interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

@Component({
  // tslint:disable-next-line
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  jwtHelper = new JwtHelperService();
  title = 'GonnaOrder';
  isIOSApp: boolean;
  isGoAdminApp: boolean;
  loader: boolean = false;
  PushNotifications: any;
  private keyboardShowListener: Promise<PluginListenerHandle>;
  iosSafeAreaOverflowHeight: number = 0;
  iosSafeAreaBottomHeight: number = 0;
  isKeyboardOpen: boolean = false;
  storesAiSafeUrl: SafeResourceUrl = null;
  isOrdersAiEnabled: boolean = false;
  // True while the AI Ordering Assistant chat panel is open. Drives the
  // `.expanded` modifier on the `.ai-widget-frame` iframe (see
  // app.component.scss) so the iframe element wraps only the visible
  // chat-bubble icon while collapsed and grows to the full panel size
  // only while expanded. State is driven by AI_ORDER_LAUNCHER_STATE
  // postMessage envelopes emitted by the iframe content (see
  // ngOnInit handler and the matching contract in
  // stores-ai-ordering-frontend/src/bridge/postToHost.ts). Card #9733.
  aiWidgetLauncherState: 'collapsed' | 'launcher' | 'chat' = 'collapsed';
  currentOrderUuid: string = '';
  storeViewState: StoreViewState;

  private keyboardHideListener: Promise<PluginListenerHandle>;
  private resizeListener: () => void;
  private scrollTimeout: any;
  private keyboardShowTimeout: any;
  private keyboardHideTimeout: any;
  private safeAreaListener: Promise<PluginListenerHandle>;
  private safeAreaInsets: SafeAreaInsets = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  };

  constructor(
    private router: Router,
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef,
    private store: Store<any>,
    private viewportService: ViewportService,
    private actions$: Actions,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private translateSer: TranslateService,
    private platform: Platform,
    private helperService: HelperService,
    private trackingService: TrackingService,
    private whitelabelService: WhitelabelService,
    private logger: LogService,
    private storesService: StoresService,
    private renderer: Renderer2,
    private messageService: MessageService,
    private mobileNotificationService: MobilePushNotificationService,
    private adminBarcodeScannerService: AdminBarcodeScannerService,
    private overlay: Overlay,
    private sanitizer: DomSanitizer,
    private destroyRef: DestroyRef,
    private paymentRedirectService: PaymentRedirectService,
    private locationService: LocationService,
    // Injected to eagerly construct the bridge so its store subscription
    // starts running at app startup. The service is a per-store-gated no-op
    // for tenants/stores that have not enabled the AI Ordering Assistant.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private aiWidgetBridgeService: AiWidgetBridgeService,
  ) {
    this.platform.ready().then(() => {
      this.initializeApp();
    });
    this.isIOSApp = Capacitor.getPlatform() === 'ios';
    this.isGoAdminApp = helperService.isGoAdminApp();

    if (environment.mode !== APPMODE.Web) {
      this.platform.ready().then(async () => {
        if (Capacitor.getPlatform() === 'android') {
          await SplashScreen.show({
            showDuration: 3000,
            autoHide: true,
          });

          // Android 16 enforces edge-to-edge. The capawesome plugin applies the
          // system-bar insets to the WebView natively, keeping content boxed
          // between the bars (no clipping). Bar colors come from capacitor.config
          // (EdgeToEdge.statusBarColor / navigationBarColor).
          await EdgeToEdge.enable();
          // The plugin sets bar COLORS but not icon contrast — set it per bar:
          // light icons on the black status bar, dark icons on the white nav bar.
          await SystemBars.setStyle({
            style: SystemBarsStyle.Dark,
            bar: SystemBarType.StatusBar,
          });
          await SystemBars.setStyle({
            style: SystemBarsStyle.Light,
            bar: SystemBarType.NavigationBar,
          });
        }
        this.initializeDeepLinks();

        if (helperService.isGoAdminApp()) {
          this.PushNotifications =
            this.mobileNotificationService.PushNotification;

          this.PushNotifications.addListener(
            'pushNotificationReceived',
            (notification) => this.pushNotificationReceived(notification),
          );
          this.PushNotifications.addListener(
            'pushNotificationActionPerformed',
            (action) => this.pushNotificationActionPerformed(action),
          );
        }
      });
    }

    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        browserRefresh = !router.navigated;
      }
    });
  }

  private async initializeApp() {
    const deviceInfo = await Device.getInfo();
    if ((deviceInfo as unknown as DeviceInfo).platform !== 'web') {
      try {
        await SocialLogin.initialize({
          google: {
            webClientId: environment.googleClientId,
            iOSClientId: environment.googleClientId,
            mode: 'online',
          },
          facebook: {
            appId: environment.facebookClientId,
            clientToken: environment.facebookClientToken,
          },
        });
      } catch (error) {
        console.error('Error initializing Google Auth:', error);
      }
    } else {
      console.warn('Google Auth not initialized: Unsupported platform');
    }
  }
  private updateViewportHeight(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  private scrollSpecialNoteWithinItemDetailsContainer(
    alignToTop: boolean = false,
  ): boolean {
    const specialNoteElement = document.getElementById('specialNote');
    if (!(specialNoteElement instanceof HTMLTextAreaElement)) {
      return false;
    }

    const scrollableContainer = specialNoteElement.closest('.overflow-auto');
    return this.helperService.scrollElementIntoContainer({
      container:
        scrollableContainer instanceof HTMLElement ? scrollableContainer : null,
      element: specialNoteElement,
      alignToTop,
      behavior: 'auto',
    });
  }
  async ngOnInit() {
    this.resizeListener = () => this.updateViewportHeight();
    window.addEventListener('resize', this.resizeListener);

    this.addScrollListener();

    if (
      this.helperService.isMobileApp() &&
      this.whitelabelService.getEventManagementEnabled()
    ) {
      await this.adminBarcodeScannerService.requestPermissions();
    }

    if (this.isIOSApp) {
      this.keyboardShowListener = Keyboard.addListener(
        'keyboardDidShow',
        () => {
          this.ngZone.run(() => {
            this.isKeyboardOpen = true;
            this.applySafeAreaInsets();
          });
          // Clear any existing timeout before setting new one
          if (this.keyboardShowTimeout) {
            cancelAnimationFrame(this.keyboardShowTimeout);
          }
          this.keyboardShowTimeout = window.requestAnimationFrame(() => {
            const activeElement = document.activeElement;
            if (activeElement instanceof HTMLElement) {
              if (
                activeElement.tagName === 'TEXTAREA' &&
                activeElement.id === 'specialNote'
              ) {
                const wasContainerScrollHandled =
                  this.scrollSpecialNoteWithinItemDetailsContainer(true);
                if (!wasContainerScrollHandled) {
                  activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                  });
                } else {
                  window.requestAnimationFrame(() => {
                    this.scrollSpecialNoteWithinItemDetailsContainer(true);
                  });
                  if (window.visualViewport) {
                    const syncWithViewport = () => {
                      this.scrollSpecialNoteWithinItemDetailsContainer(true);
                    };
                    window.visualViewport.addEventListener(
                      'resize',
                      syncWithViewport,
                      { once: true },
                    );
                    window.visualViewport.addEventListener(
                      'scroll',
                      syncWithViewport,
                      { once: true },
                    );
                  }
                }
              } else {
                activeElement.scrollIntoView({
                  behavior: 'auto',
                  block: 'nearest',
                  inline: 'nearest',
                });
              }
            }
            this.keyboardShowTimeout = null;
          });
        },
      );

      this.keyboardHideListener = Keyboard.addListener(
        'keyboardDidHide',
        () => {
          this.ngZone.run(() => {
            this.isKeyboardOpen = false;
            this.applySafeAreaInsets();
          });
          // Clear any existing timeout before setting new one
          if (this.keyboardHideTimeout) {
            cancelAnimationFrame(this.keyboardHideTimeout);
          }
          this.keyboardHideTimeout = window.requestAnimationFrame(() => {
            this.updateViewportHeight();
            this.keyboardHideTimeout = null;
          });
        },
      );
    }

    //Message Service For Sign In
    this.messageService.receiveMessage().subscribe(async (message) => {
      if (message === 'appleSignIn') {
        this.appleSignIn();
      } else if (message === 'googleSignIn') {
        this.signInWithGoogle();
      } else if (message === 'facebookLogin') {
        this.facebookSignIn();
      }
    });
    console.log('checking for app version');
    if (this.swUpdate.isEnabled) {
      console.log('service worker enabled');

      this.ngZone.runOutsideAngular(() => {
        interval(30 * 1000).subscribe(() => {
          this.ngZone.run(() => {
            console.log('checking for updates');
            this.swUpdate.checkForUpdate();
          });
        });

        this.swUpdate.versionUpdates
          .pipe(
            filter(
              (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY',
            ),
          )
          .subscribe((evt) => {
            this.ngZone.run(() => {
              console.log('New version detected');
              console.log('Current:', evt.currentVersion.hash);
              console.log('Latest:', evt.latestVersion.hash);

              this.store.dispatch(
                new NewServiceWorkerVersion(
                  evt.currentVersion.hash,
                  evt.latestVersion.hash,
                ),
              );

              this.swUpdate.activateUpdate().then(() => {
                console.log('Update activated');
              });
            });
          });
      });
    }

    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      this.trackingService.trackMetaPageViewIfUrlChanged();
      window.scrollTo(0, 0);
    });

    // disable zoom in for ios
    this.viewportService.applyCustomViewportForIOS();
    this.viewportService.applyResizesContentViewportForAndroidTemplateApp();

    if (
      environment.mode == APPMODE.Web ||
      environment.mode == APPMODE.Admin ||
      environment.mode == APPMODE.Template ||
      environment.mode == APPMODE.Customer ||
      environment.mode == APPMODE.Kiosk
    ) {
      this.title = this.whitelabelService.getWhiteLabelName();
      if (document.readyState !== 'loading') {
        this.setBrandColors();
      } else {
        document.addEventListener('DOMContentLoaded', () =>
          this.setBrandColors(),
        );
      }
    }

    if (this.isIOSApp) {
      SafeArea.getSafeAreaInsets().then(
        ({ insets }: { insets: SafeAreaInsets }) => {
          this.setSafeAreaInsets(insets);
        },
      );

      // Track SafeArea listener for cleanup
      this.safeAreaListener = SafeArea.addListener(
        'safeAreaChanged',
        (data: { insets: SafeAreaInsets }) => {
          const { insets } = data;
          this.setSafeAreaInsets(insets);
        },
      );
    }

    this.isOrdersAiEnabled = this.whitelabelService.isTenantOrdersAiEnabled();

    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getCurrentCartState),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([selectedStore, cartState]) => {
          const shouldShow = this.shouldShowOrdersAi(selectedStore);

          if (shouldShow && this.currentOrderUuid != cartState.uuid) {
            this.setupStoresAi(selectedStore, cartState);
            return;
          }

          if (!shouldShow && this.storesAiSafeUrl) {
            // Settings flipped to "hide" while the widget was on screen
            // (e.g. admin disabled Ordering or Shopping Basket
            // mid-session). Tear down the iframe and reset the uuid
            // guard so the widget rebuilds cleanly if the toggles
            // flip back on. See Trello #9734.
            this.storesAiSafeUrl = null;
            this.currentOrderUuid = '';
          }
        },
      });

    this.store
      .select(getCurrentStoreViewStatus)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged((prev, curr) => {
          return prev?.state == curr?.state;
        }),
      )
      .subscribe((storeViewState) => (this.storeViewState = storeViewState));

    // Listen for launcher state messages from the AI Ordering Assistant
    // iframe so we can grow / shrink the iframe element's bounding box.
    // The iframe content (stores-ai-ordering-frontend) emits an
    // AI_ORDER_LAUNCHER_STATE envelope whenever the chat opens or closes,
    // plus once at mount with the initial state. Card #9733.
    //
    // Origin validation: the iframe is served from
    // `order-ai.<whitelabel-domain>` (see setupStoresAi below). We reject
    // any postMessage whose origin doesn't match, which closes the door
    // on a malicious same-window page trying to flip our class. The
    // listener is registered unconditionally — the typed discriminator
    // is enough to ignore unrelated messages and the per-message work
    // is constant. Subscription teardown follows the same
    // takeUntilDestroyed pattern as the other RxJS streams above.
    fromEvent<MessageEvent>(window, 'message')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event?.data?.type !== 'AI_ORDER_LAUNCHER_STATE') {
          return;
        }
        const expectedOrigin = `https://order-ai.${this.whitelabelService.getWhiteLabelDomainUri()}`;
        if (event.origin !== expectedOrigin) {
          return;
        }
        const state = event.data.state;
        this.aiWidgetLauncherState =
          state === 'chat' || state === 'launcher' ? state : 'collapsed';
      });
  }

  async ngOnDestroy() {
    // Clear keyboard listener timeouts
    if (this.keyboardShowTimeout) {
      cancelAnimationFrame(this.keyboardShowTimeout);
    }
    if (this.keyboardHideTimeout) {
      cancelAnimationFrame(this.keyboardHideTimeout);
    }

    // Remove keyboard listeners
    if (this.keyboardShowListener) {
      (await this.keyboardShowListener).remove();
    }
    if (this.keyboardHideListener) {
      (await this.keyboardHideListener).remove();
    }

    // Remove SafeArea listener
    if (this.safeAreaListener) {
      (await this.safeAreaListener).remove();
    }

    // Remove resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }

    this.removeScrollListener();
    this.messageService.cleanup(); // Clean up message service listeners
  }
  private setSafeAreaInsets(insets: SafeAreaInsets) {
    this.safeAreaInsets = {
      top: insets.top ?? 0,
      bottom: insets.bottom ?? 0,
      left: insets.left ?? 0,
      right: insets.right ?? 0,
    };
    this.applySafeAreaInsets();
  }

  private applySafeAreaInsets(): void {
    const bottomInset = this.isKeyboardOpen ? 0 : this.safeAreaInsets.bottom;
    this.iosSafeAreaOverflowHeight = this.safeAreaInsets.top;
    this.iosSafeAreaBottomHeight = bottomInset;

    const appliedInsets: SafeAreaInsets = {
      ...this.safeAreaInsets,
      bottom: bottomInset,
    };

    (
      Object.entries(appliedInsets) as Array<[keyof SafeAreaInsets, number]>
    ).forEach(([key, value]) => {
      document.documentElement.style.setProperty(
        `--safe-area-inset-${key}`,
        `${value}px`,
      );
    });
  }

  private async signInWithGoogle() {
    try {
      const result = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['email', 'profile'],
          forcePrompt: true,
          style: 'standard',
          autoSelectEnabled: false,
          filterByAuthorizedAccounts: false,
          forceRefreshToken: true,
        },
      });
      if (result.result.responseType === 'online') {
        const googleResponse = result.result as GoogleLoginResponseOnline;

        const user = {
          provider: 'google',
          accessToken: googleResponse.idToken,
          email: googleResponse.profile.email,
          firstName: googleResponse.profile.givenName,
          lastName: googleResponse.profile.familyName,
          mode: this.helperService.isGoAdminApp()
            ? 'admin-login'
            : 'customer-login',
        };

        window.postMessage(user, `${window.location.origin}/login`);
        // Handle user information, e.g., send to your backend or save locally
      }
    } catch (error) {
      console.error('Error during Google sign-in:', error);
    }
  }

  private async appleSignIn() {
    let redirectURI = window.location.href;
    if (!redirectURI.startsWith('https://')) {
      console.error('Redirect URI is not HTTPS');
      // Handle non-HTTPS scenario
    } else {
      const uriParts = redirectURI.split('?');
      redirectURI = uriParts[0];
    }
    const rawNonce = 'nonce';
    const hashedNonceHex = await this.helperService.isSha256(rawNonce);
    let options: SignInWithAppleOptions = {
      clientId: environment.appleClientId,
      redirectURI,
      scopes: 'email name',
      nonce: hashedNonceHex,
    };
    try {
      const response: SignInWithAppleResponse =
        await SignInWithApple.authorize(options);
      if (
        response?.response?.identityToken &&
        response?.response?.authorizationCode
      ) {
        const appleToken = this.jwtHelper.decodeToken(
          response.response.identityToken,
        );
        const socialAccountDetails = {
          provider: 'apple',
          accessToken: response.response.identityToken,
          appleCode: response.response.authorizationCode,
          email: appleToken.email,
          firstName: response.response.givenName,
          lastName: response.response.familyName,
          mode: this.helperService.isGoAdminApp()
            ? 'admin-login'
            : 'customer-login',
        };
        window.postMessage(
          socialAccountDetails,
          `${window.location.origin}/login`,
        );
      }
    } catch (err) {
      console.error('Sign in failed:', err);
      window.frames[0].postMessage({ status: 'error', error: err }, '*');
    }
  }

  private async facebookSignIn() {
    try {
      const result = await SocialLogin.login({
        provider: 'facebook',
        options: {
          permissions: ['email', 'public_profile'],
          limitedLogin: this.isIOSApp ? true : false,
        },
      });

      let user;

      if (Capacitor.getPlatform() === 'ios') {
        // iOS: Use the response directly
        user = this.extractFacebookUserData(
          result.result,
          null,
          this.helperService.isGoAdminApp(),
        );
      } else {
        // Android: Fetch additional profile data
        const profileData = await SocialLogin.providerSpecificCall({
          call: 'facebook#getProfile',
          options: {
            fields: ['id', 'name', 'email', 'first_name', 'last_name'],
          },
        });

        user = this.extractFacebookUserData(
          result.result,
          profileData.profile,
          this.helperService.isGoAdminApp(),
        );
      }

      // Send the user data via postMessage
      window.postMessage({ ...user }, `${window.location.origin}/login`);
    } catch (error) {
      console.error('Error during Facebook sign-in:', error);
    }
  }

  private extractFacebookUserData(
    facebookResponse: any,
    providerProfile?: any,
    isAdminApp: boolean = false,
  ): any {
    // Determine the platform
    const isIOS = Capacitor.getPlatform() === 'ios';

    // Extract profile data based on platform
    const profile = facebookResponse.profile || {};
    const additionalProfile = isIOS
      ? facebookResponse.profile || {}
      : providerProfile || {};

    // Extract common fields with fallbacks
    const email =
      additionalProfile?.email ||
      profile?.email ||
      providerProfile?.email ||
      '';

    const fullName =
      additionalProfile?.name || profile?.name || providerProfile?.name || '';

    const firstName = this.helperService.extractFirstName(fullName);
    const lastName = this.helperService.extractLastName(fullName);

    // Build the user object
    const user = {
      provider: 'facebook',
      accessToken: isIOS
        ? facebookResponse.idToken
        : facebookResponse?.accessToken?.token,
      email: email,
      firstName: firstName,
      lastName: lastName,
      fullName: fullName,
      imageUrl: isIOS
        ? profile?.imageURL || ''
        : providerProfile?.picture?.data?.url || profile?.imageURL || '',
      mode: isAdminApp ? 'admin-login' : 'customer-login',
    };

    // Add Facebook ID for Android
    if (!isIOS) {
      user['facebookId'] = profile?.userID || providerProfile?.id || '';
    }

    return user;
  }

  private initializeDeepLinks() {
    App.addListener('appUrlOpen', (data) => {
      const url = data.url;
      let route = this.extractRouteFromUrl(url);
      let routeType = this.extractRouteType(route);

      this.ngZone.run(() => {
        switch (routeType) {
          case 'api':
            this.handleApiCall(route);
            break;
          case 'thankyou-redirect':
            this.handleThankyouRedirect(route);
            break;
          case 'stripe-verification':
            this.router.navigate([route.path], {
              queryParams: route.queryParams,
            });
            break;
          case 'customer-app':
            this.handleCustomerApp(route, route.fragment);
            break;
          case 'template-app':
            this.handleTemplateApp(route);
            break;
          case 'payment-redirect':
            this.router.navigateByUrl(route.path);
            break;
          case 'standalone-payment':
            this.handleStandalonePayment(route);
            break;
          case 'payment-error':
            this.handleGeneralRedirect(route);
            break;
          default:
            console.error('No Route Matched', url);
        }

        this.paymentRedirectService.closedByRedirection = true;
        this.paymentRedirectService.closeMobileBrowser();
      });
    });
  }

  private extractRouteFromUrl(url: string): DeeplinkRoute {
    let urlObj = new URL(url);
    const urlReplaced = urlObj.href.split(`${urlObj.protocol}//`)[1];
    urlObj = new URL(`https://${urlReplaced}`);
    const fragment = urlObj.hash.substring(1);
    const schemeSegments = url.split('://')[0].split('com.gonnaorder.');

    // Define recognized environments
    const recognizedEnvironments = [
      'go',
      'goadmin',
      'godev',
      'goadmindev',
      'kioskdev',
      'kiosk',
    ];

    // Detecting the environment or template based on the scheme
    let appEnvironment, storeAlias;

    if (
      recognizedEnvironments.some((env) => schemeSegments[1]?.includes(env))
    ) {
      appEnvironment = schemeSegments[1]; // Assuming the second part is the environment for customer apps
      const subdomainSegments = urlObj.hostname.split('.');
      storeAlias = subdomainSegments[0]; // Assuming the first segment is always the store alias for customer apps
    } else {
      // If no known environment, it's likely a template app, so the store alias is the second segment
      storeAlias = environment.templateStoreAlias;
      appEnvironment = 'template'; // Indicate that this is a template app environment
    }
    return {
      path: urlObj.pathname,
      queryParams: this.parseQueryParams(urlObj.search),
      fragment,
      storeAlias,
      appEnvironment,
      url: urlObj.href,
    };
  }

  private parseQueryParams(search: string) {
    const params = new URLSearchParams(search);
    const queryParams = {};
    params.forEach((value, key) => {
      queryParams[key] = value;
    });
    return queryParams;
  }

  private extractRouteType(route: any) {
    if (route.path.includes('/api')) {
      return 'api';
    } else if (route.path.includes('/stripe/verification')) {
      return 'stripe-verification';
    } else if (route.url.includes('thankyou')) {
      return 'thankyou-redirect';
    } else if (route.url.includes('paymentError')) {
      return 'payment-error';
    } else if (route.path.includes('/public/customer/store')) {
      return 'customer-app';
    } else if (route.path.includes('/public')) {
      return 'template-app';
    } else if (route.path.includes('payment-redirect')) {
      return 'payment-redirect';
    } else if (route.path.includes('/manager')) {
      return 'admin-app';
    } else if (route.path.includes('/standalone-payment')) {
      return 'standalone-payment';
    } else {
      return 'unknown'; //Default Case if no type matches
    }
  }

  private handleApiCall(route: DeeplinkRoute) {
    this.storesService.checkCompletePaymentStatus(route.path).subscribe(
      (res: any) => {
        const url = new URL(res.redirectUrl);
        this.router.navigateByUrl(url.pathname);
      },
      (err) => {
        this.handleGeneralRedirect(route);
      },
    );
  }

  private handleThankyouRedirect(route: DeeplinkRoute) {
    if (this.helperService.isGoKioskApp()) {
      this.handleGeneralRedirect(route);
    } else if (this.helperService.isGoApp()) {
      this.handleCustomerApp(route, route.fragment);
    } else if (this.helperService.isGoTemplateApp()) {
      this.handleTemplateApp(route);
    } else if (this.helperService.isGoAdminApp()) {
      this.store.dispatch(
        new ClearIntegrationPayment({
          src: 'StoreCheckoutPaymentComponent',
          description: `Payment Status: INITIAL`,
        }),
      );
      this.router
        .navigateByUrl('/manager', { skipLocationChange: true })
        .then(() => {
          this.router.navigateByUrl(
            `${route.path}${route.fragment ? `#${route.fragment}` : ''}`,
          );
        });
    }
  }

  private handleGeneralRedirect(route: DeeplinkRoute) {
    this.router.navigateByUrl('/', { skipLocationChange: false }).then(() => {
      setTimeout(() => {
        this.router.navigateByUrl(`${this.router.url}#${route.fragment}`);
      }, 1000);
    });
  }

  private handleCustomerApp(route: DeeplinkRoute, fragment: string) {
    const storeAlias = route.storeAlias;
    this.actions$
      .pipe(
        ofType<SaveStoreByAliasSuccess>(
          StoreActionType.SaveStoreByAliasSuccess,
        ),
        filter((action) => action.saveMode === StoreSaveMode.DEEPLINK),
        take(1),
      )
      .subscribe((action) => {
        if (action.storeAlias) {
          this.ngZone.run(() => {
            this.store.dispatch(new SetMobileStore(action.storeAlias));
            this.router
              .navigateByUrl('/public/customer', { skipLocationChange: true })
              .then(() => {
                this.router.navigateByUrl(
                  `/public/customer/store/${action.storeAlias}${fragment ? `#${fragment}` : ''}`,
                );
              });
          });
        }
      });
    this.actions$
      .pipe(
        ofType<SaveStoreByAliasFailed>(StoreActionType.SaveStoreByAliasFailed),
        take(1),
      )
      .subscribe((action) => {
        this.ngZone.run(() =>
          this.toastr.info(
            this.translateSer.instant('public.main.storeNotFound'),
            this.translateSer.instant('public.global.information'),
          ),
        );
      });

    if (
      route.fragment.includes('thankyou') ||
      route.fragment.includes('cart')
    ) {
      this.store.dispatch(
        new ClearIntegrationPayment({
          src: 'StoreCheckoutPaymentComponent',
          description: `Payment Status: INITIAL`,
        }),
      );
    }

    this.store.dispatch(
      new SaveStoreByAlias(
        storeAlias,
        `https://${storeAlias}.${environment.envDomain}`,
        StoreSaveMode.DEEPLINK,
      ),
    );
  }

  private handleTemplateApp(route: DeeplinkRoute) {
    if (
      environment.templateStoreAlias &&
      environment.templateStoreAlias == route.storeAlias
    ) {
      this.ngZone.run(() => {
        this.store.dispatch(
          new ClearIntegrationPayment({
            src: 'StoreCheckoutPaymentComponent',
            description: `Payment Status: INITIAL`,
          }),
        );
        let baseUrl = '/';
        if (route.path.includes('/capture')) {
          baseUrl = '/manager';
        }
        this.router
          .navigateByUrl(baseUrl, { skipLocationChange: true })
          .then(() => {
            this.router.navigateByUrl(
              `${route.path}${route.fragment ? `#${route.fragment}` : ''}`,
            );
          });
      });
    }
  }

  private handleStandalonePayment(route: DeeplinkRoute) {
    this.router.navigateByUrl('/', { skipLocationChange: false }).then(() => {
      this.router.navigateByUrl(route.path);
    });
  }

  private addScrollListener(): void {
    const closeDropdowns = (event?: Event) => {
      try {
        if (event && event.target) {
          const scrollTarget = event.target as Element;

          // Don't close if scrolling within the dropdown panel itself
          if (
            scrollTarget.classList &&
            scrollTarget.classList.contains('mat-mdc-select-panel')
          ) {
            return;
          }
        }

        const openSelectPanels = document.querySelectorAll(
          '.mat-mdc-select-panel',
        );
        if (openSelectPanels.length > 0) {
          const selectBackdrops = document.querySelectorAll(
            '.cdk-overlay-backdrop',
          );
          selectBackdrops.forEach((backdrop) => {
            if (backdrop instanceof HTMLElement) {
              const overlayPane = backdrop.nextElementSibling;
              if (
                overlayPane &&
                overlayPane.querySelector('.mat-mdc-select-panel')
              ) {
                backdrop.click();
              }
            }
          });

          const matSelects = document.querySelectorAll('mat-select');
          matSelects.forEach((select) => {
            if (select && (select as any).close) {
              try {
                (select as any).close();
              } catch (e) {}
            }
          });
        }
      } catch (error) {
        console.warn('Error closing dropdowns:', error);
      }
    };

    window.addEventListener('scroll', closeDropdowns, { passive: true });
    document.addEventListener('scroll', closeDropdowns, {
      passive: true,
      capture: true,
    });
  }

  private removeScrollListener(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  private setBrandColors() {
    const primaryBrandColour =
      this.whitelabelService.getWhiteLabelPrimaryBrandColour();
    const secondaryBrandColour =
      this.whitelabelService.getWhiteLabelSecondaryBrandColour();
    // Set the custom properties for the current domain
    document.documentElement.style.setProperty(
      '--primary-brand-color',
      primaryBrandColour,
      'important',
    );
    document.documentElement.style.setProperty(
      '--secondary-brand-color',
      secondaryBrandColour,
      'important',
    );
    document.title = this.whitelabelService.getWhiteLabelName();
  }

  private pushNotificationReceived(notification) {
    let payload = notification?.data;

    if (payload) {
      this.storesService
        .list(defaultPaging, payload.storeAlias)
        .pipe(
          take(1),
          filter((result) => result.data.length > 0),
          map((result) => result.data[0]),
        )
        .subscribe((clientStore) => {
          if (clientStore) {
            this.store
              .select(getNotificationSubscriptionsByStoreId, {
                storeId: clientStore.id,
              })
              .pipe(take(1))
              .subscribe((notificationEnabled) => {
                if (
                  this.helperService.isAndroid() ||
                  this.helperService.isIOS()
                ) {
                  this.store.dispatch(
                    new OrderDirectPrint(payload, clientStore.id),
                  );
                } else {
                  this.logger.error('Not supported device');
                  return;
                }
              });
          } else {
          }
        });
    } else {
    }
  }

  private pushNotificationActionPerformed(action) {
    let payload = action?.notification?.data;

    if (payload) {
      this.storesService
        .list(defaultPaging, payload.storeAlias)
        .pipe(
          take(1),
          filter((result) => result.data.length > 0),
          map((result) => result.data[0]),
        )
        .subscribe((clientStore) => {
          if (clientStore) {
            if (this.helperService.isAndroid() || this.helperService.isIOS()) {
              if (action.actionId && action.actionId == 'tap') {
                this.ngZone.run(() => {
                  this.router
                    .navigate([`/manager/stores/${clientStore.id}/orders`])
                    .then(() => {
                      this.router.navigate([
                        `/manager/stores/${clientStore.id}/orders/${payload.uuid}`,
                      ]);
                    });
                });
              }
              this.store.dispatch(
                new OrderDirectPrint(payload, clientStore.id),
              );
            } else {
              this.logger.error('Not supported device');
              return;
            }
          } else {
          }
        });
    } else {
    }
  }

  /**
   * Predicate for whether the AI Ordering Assistant iframe should be
   * embedded for the currently-selected store. The widget mutates the
   * cart on the customer's behalf, so it is only meaningful when the
   * store both has the AI capability turned on AND lets customers
   * actually order. The two ordering-related settings are
   * default-on, so we hide the widget only on an explicit `false`.
   *
   * Conditions (AND):
   *   1. Tenant has the AI Ordering capability (whitelabel flag).
   *   2. Store has the AI Ordering Assistant toggle on.
   *   3. Store has the Ordering toggle on (`ENABLE_ORDERING !== false`).
   *   4. Store has the Shopping Basket toggle on (`BASKET_ENABLED !== false`).
   *
   * The `!== false` form matches the convention used by
   * `view-order-button.component.ts` and treats a missing setting as
   * the default-on value the GO main backend ships with.
   *
   * See Trello #9734.
   */
  private shouldShowOrdersAi(selectedStore: ClientStore | null): boolean {
    if (!this.isOrdersAiEnabled) return false;
    if (!selectedStore?.id) return false;
    const settings = selectedStore.settings;
    if (!settings?.ORDERS_AI_ENABLED) return false;
    if (settings.ENABLE_ORDERING === false) return false;
    if (settings.BASKET_ENABLED === false) return false;
    return true;
  }

  setupStoresAi(selectedStore: ClientStore, cartState: Order) {
    let baseUrl =
      'https://{domain}?storeId={storeId}&orderId={orderId}&language={language}&domain={appDomain}&refreshToken={refreshToken}';

    const availableLanguages = ['en', 'el'];
    const adminHostUrl = this.whitelabelService.getAdminHostURL();
    const env =
      environment.name == 'production'
        ? 'prod'
        : environment.name == 'staging'
          ? 'staging'
          : 'dev';
    const storeId = selectedStore.id;
    let language = this.translateSer.currentLang;
    let domainUri = `order-ai.${this.whitelabelService.getWhiteLabelDomainUri()}`;

    if (!availableLanguages.includes(language)) {
      language = 'en';
    }

    // Per-page-load nonce so the AI ordering backend invalidates its
    // cached catalog the first time it sees this token (card #9803).
    // crypto.randomUUID exists on all evergreen browsers and on
    // iOS 15.4+ / Android Chromium WebViews shipped from late 2022;
    // the fallback covers older Capacitor builds running on legacy
    // OS versions. The token is not a secret — uniqueness per
    // page-load is the only requirement.
    const refreshToken =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

    const url = baseUrl
      .replace('{domain}', domainUri)
      .replace('{orderId}', cartState.uuid)
      .replace('{storeId}', storeId.toString())
      .replace('{language}', language)
      .replace('{appDomain}', adminHostUrl)
      .replace('{refreshToken}', refreshToken);

    this.storesAiSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.currentOrderUuid = cartState.uuid;
  }

  isStoreCatalogPage() {
    return this.storeViewState?.state === 'LOADED';
  }

  /**
   * Whether the AI Ordering Assistant iframe should be rendered for the
   * current route.
   *
   * The widget is visible on customer-storefront routes (the public site
   * the customer uses to browse and order) and on the Admin Order Capture
   * page (the kiosk-like admin flow under `/manager/.../capture/...`).
   *
   * It must NOT be visible elsewhere in the admin shell — without this
   * guard, the iframe stays mounted at the root `<body>` selector once the
   * store view state has reached `LOADED` and follows the admin around
   * Settings / Catalog / Marketing pages (card #9684 defect B).
   *
   * The existing tenant / per-store gates upstream of this method
   * (`isOrdersAiEnabled && storesAiSafeUrl` in the template) keep this
   * predicate cheap for non-AI tenants: the `@if` short-circuits before
   * this method runs.
   */
  shouldShowAiWidget(): boolean {
    const url = this.router.url ?? '';
    const isAdminShell = url.includes('/manager/');
    if (!isAdminShell) {
      return true;
    }
    return this.locationService.isAdminOrderCaptureUpdate();
  }
}
